import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="pdf-export"
export default class extends Controller {
  static values = {
    statusUrl: String,
    generateUrl: String
  }

  static targets = ["wrapper", "status", "buttonText"]

  connect() {
    this.pollingInterval = null
    this.maxPollingAttempts = 60 // Max 60 attempts (2 minutes with 2s interval)
    this.pollingAttempts = 0
    this.originalButtonText = null
  }

  disconnect() {
    this.stopPolling()
  }

  // Export to PDF with background job and polling
  async export(event) {
    event.preventDefault()

    if (!this.generateUrlValue || !this.statusUrlValue) {
      return
    }

    // Disable button to prevent multiple clicks
    const button = event.currentTarget
    if (button.disabled) return

    this.disableButton(button)
    this.showLoading()

    try {
      // Always generate new PDF (don't check status first)
      // This is because insights PDFs are generated on-demand each time
      await this.requestPdfGeneration()
    } catch (error) {
      this.showError("Failed to export PDF. Please try again.")
    } finally {
      // Re-enable button after completion
      this.enableButton(button)
    }
  }

  async checkStatus() {
    const response = await fetch(this.statusUrlValue, {
      headers: {
        'Accept': 'application/json'
      }
    })
    const data = await response.json()
    return data.status
  }

  async requestPdfGeneration() {
    // Request PDF binary directly — avoids exposing raw R2 URLs to browser navigation
    const response = await fetch(this.generateUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'application/pdf',
        'X-CSRF-Token': this.getCsrfToken()
      }
    })

    const contentType = response.headers.get('content-type') || ''

    if (contentType.includes('application/pdf')) {
      // Got PDF binary directly (insights endpoints)
      const blob = await response.blob()
      this.openBlobInNewTab(blob)
      this.showSuccess('PDF ready!')
      this.hideLoading()
      return
    }

    // JSON response — generation queued or PDF already exists
    const data = await response.json()

    if (data.status === 'queued' || data.status === 'working') {
      this.startPolling()
    } else if (data.status === 'ready') {
      await this.downloadPdf()
    } else if (data.status === 'error' || data.error) {
      this.showError(data.error || data.message || 'Failed to generate PDF')
    }
  }

  startPolling() {
    this.pollingAttempts = 0
    this.updateStatus('Generating PDF...')

    this.pollingInterval = setInterval(async () => {
      this.pollingAttempts++

      try {
        const response = await fetch(this.statusUrlValue, {
          headers: {
            'Accept': 'application/json'
          }
        })
        const data = await response.json()

        if (data.status === 'ready') {
          this.stopPolling()
          await this.downloadPdf()
        } else if (this.pollingAttempts >= this.maxPollingAttempts) {
          this.stopPolling()
          this.showError('PDF generation is taking longer than expected. Please try again later.')
        }
      } catch (error) {
        this.stopPolling()
        this.showError('Failed to check PDF status')
      }
    }, 2000) // Poll every 2 seconds
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  // Download PDF binary from status endpoint (proxied through Rails, not raw R2)
  async downloadPdf() {
    const response = await fetch(this.statusUrlValue, {
      headers: {
        'Accept': 'application/pdf'
      }
    })

    if (!response.ok) {
      this.showError('PDF not available')
      return
    }

    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/pdf')) {
      const blob = await response.blob()
      this.openBlobInNewTab(blob)
      this.showSuccess('PDF ready!')
      this.hideLoading()
    } else {
      this.showError('PDF not available')
    }
  }

  openBlobInNewTab(blob) {
    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')
    // Revoke after a delay to allow the new tab to load the blob
    setTimeout(() => window.URL.revokeObjectURL(url), 60000)
  }

  showLoading() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.style.opacity = '0.5'
      this.wrapperTarget.style.pointerEvents = 'none'
    }
  }

  hideLoading() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.style.opacity = '1'
      this.wrapperTarget.style.pointerEvents = 'auto'
    }
  }

  updateStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.remove('hidden')
    }
  }

  showSuccess(message) {
    this.updateStatus(message)
    setTimeout(() => {
      if (this.hasStatusTarget) {
        this.statusTarget.classList.add('hidden')
      }
    }, 3000)
  }

  showError(message) {
    this.hideLoading()
    this.updateStatus(message)
    alert(message) // Show alert for errors
  }

  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  disableButton(button) {
    button.disabled = true

    // Find the wrapper span (contains both icon and text)
    const wrapperSpan = button.querySelector('span.flex')
    if (wrapperSpan) {
      // Store original HTML for restoration
      this.originalButtonHTML = wrapperSpan.innerHTML

      // Replace with loading state (spinner + text)
      wrapperSpan.innerHTML = `
        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Generating...
      `
    }
  }

  enableButton(button) {
    button.disabled = false

    // Restore original button HTML
    const wrapperSpan = button.querySelector('span.flex')
    if (wrapperSpan && this.originalButtonHTML) {
      wrapperSpan.innerHTML = this.originalButtonHTML
      this.originalButtonHTML = null
    }
  }
}
